import React, { useEffect, useState } from 'react';
import API from '../../api';
import { API_IMAGE_URL } from '../../lib/api';

export default function MyAttendance({ employeeId: propEmployeeId }) {
  const [history, setHistory] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const getEmployeeId = () => {
    if (propEmployeeId) return propEmployeeId;
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.id;
    } catch (err) {
      return null;
    }
  };

  const employeeId = getEmployeeId();

  useEffect(() => {
    if (employeeId) fetchAttendance();
  }, [employeeId, month, year]);

  const fetchAttendance = async () => {
    try {
      const res = await API.get(`/attendance/history/${employeeId}?month=${month}&year=${year}`);
      setHistory(res.data.data);
      calculateTotalMinutes(res.data.data);
    } catch (err) {
      console.error('Error fetching attendance:', err);
    }
  };

  const calculateTotalMinutes = (data) => {
    let total = 0;
    data.forEach(item => {
      if (item.working_hours) {
        const [h, m] = item.working_hours.split(' ').map(v => parseInt(v));
        total += (h * 60) + m;
      }
    });
    setTotalMinutes(total);
  };

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  const totalDays = (totalMinutes / 480).toFixed(2); // 8 hours = 480 mins

  const formatDate = (dateStr) => {
    const dateObj = new Date(dateStr);
    return dateObj.toLocaleDateString('en-GB'); // DD-MM-YYYY
  };

  const workingHoursClass = (working_hours) => {
    if (!working_hours) return 'bg-red-600 text-white';
    const [h, m] = working_hours.split('h').map(s => parseInt(s.trim()) || 0);
    const totalHrs = h + m / 60;
    if (totalHrs < 4) return 'bg-red-600 text-white';
    if (totalHrs < 7) return 'bg-yellow-400 text-black';
    return 'bg-green-600 text-white';
  };

  return (
    <div className="shadow-md rounded-lg mt-4 overflow-auto bg-white max-w-full">
      <div className="bg-blue-700 text-white px-4 py-2 rounded-t-lg flex justify-between items-center">
        <h6 className="text-lg font-semibold">My Attendance History</h6>
        <div className="flex space-x-2 text-sm text-black">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="rounded px-2 py-1"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded px-2 py-1"
          >
            {[...Array(5)].map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <option key={i} value={y}>{y}</option>;
            })}
          </select>
        </div>
      </div>

      <div className="p-4">
        <table className="min-w-full border border-gray-300 text-center table-fixed">
          <thead className="bg-blue-100 text-gray-700">
            <tr>
              <th className="border border-gray-300 px-2 py-1 w-10">S.N.</th>
              <th className="border border-gray-300 px-2 py-1">Date</th>
              <th className="border border-gray-300 px-2 py-1">Entry</th>
              <th className="border border-gray-300 px-2 py-1">Exit</th>
              <th className="border border-gray-300 px-2 py-1">Entry Photo</th>
              <th className="border border-gray-300 px-2 py-1">Exit Photo</th>
              <th className="border border-gray-300 px-2 py-1">Working Hours</th>
              <th className="border border-gray-300 px-2 py-1">Remark</th>
              <th className="border border-gray-300 px-2 py-1">Approved</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan="9" className="py-4 text-center text-gray-500">
                  No attendance records found.
                </td>
              </tr>
            ) : (
              history.map((r, index) => (
                <tr key={index} className="odd:bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1">{index + 1}</td>
                  <td className="border border-gray-300 px-2 py-1">{formatDate(r.date)}</td>
                  <td className="border border-gray-300 px-2 py-1">{r.entry_time || '-'}</td>
                  <td className="border border-gray-300 px-2 py-1">{r.exit_time || '-'}</td>
                  <td className="border border-gray-300 px-2 py-1">
                    {r.entry_photo && (
                      <img
                        src={`${API_IMAGE_URL}${r.entry_photo}`}
                        alt="Entry"
                        className="mx-auto w-16 h-24 object-cover rounded"
                      />
                    )}
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    {r.exit_photo && (
                      <img
                        src={`${API_IMAGE_URL}${r.exit_photo}`}
                        alt="Exit"
                        className="mx-auto w-16 h-24 object-cover rounded"
                      />
                    )}
                  </td>
                  <td className={`border border-gray-300 px-2 py-1 ${workingHoursClass(r.working_hours)}`}>
                    {r.working_hours || '0'}
                  </td>
                  <td className="border border-gray-300 px-2 py-1">{r.remark || '-'}</td>
                  <td className="border border-gray-300 px-2 py-1">
                    {r.is_approved ? (
                      <span className="inline-block bg-green-600 text-white px-2 py-1 rounded font-semibold">Yes</span>
                    ) : (
                      <span className="inline-block bg-yellow-300 text-black px-2 py-1 rounded font-semibold">No</span>
                    )}
                    {r.attendance_type === 'full_day' && (
                      <span className="bg-green-200 px-2 py-1 rounded font-semibold ms-1">Full Day</span>
                    )}
                    {r.attendance_type === 'half_day' && (
                      <span className="bg-yellow-200 px-2 py-1 rounded font-semibold ms-1">Half Day</span>
                    )}
                    {r.attendance_type === 'absent' && (
                      <span className="bg-red-600 text-white px-2 py-1 rounded font-semibold ms-1">Absent</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="mt-4 text-gray-700 text-sm font-medium">
          <p><strong>Total Hours This Month:</strong> {totalHours}h {remainingMinutes}m</p>
          <p><strong>Equivalent to Working Days (8h/day):</strong> {totalDays} days</p>
        </div>
      </div>
    </div>
  );
}
